import { Check, Copy, PartyPopper } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { Logo } from "@/components/logo";
import { NO_ACCOUNT_NOTE } from "@/components/settings/license-panel";
import { LoadingRegion } from "@/components/skeletons";
import { EmptyState, ErrorState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { licenseReturnPath, useClaimLicense } from "@/hooks/use-license";
import { track } from "@/lib/analytics";
import { storeLicense } from "@/lib/license";

function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">{children}</div>
    </main>
  );
}

/** Where Polar sends the buyer after paying: turns the checkout into a key for this browser. */
export function LicenseSuccessPage() {
  const [params] = useSearchParams();
  const checkoutId = params.get("checkout_id") ?? "";
  const claim = useClaimLicense(checkoutId);
  const key = claim.data;
  const [copied, setCopied] = useState(false);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (key) return;
    const timer = window.setTimeout(() => setSlow(true), 20_000);
    return () => window.clearTimeout(timer);
  }, [key]);

  useEffect(() => {
    if (!key) return;
    storeLicense(key);
    track("license_purchased");
  }, [key]);

  if (!checkoutId) {
    return (
      <Shell>
        <EmptyState
          title="No purchase to show"
          description="This page is where you land after buying a license."
          testId="license-empty"
          action={
            <Button asChild variant="outline">
              <Link to="/">Back home</Link>
            </Button>
          }
        />
      </Shell>
    );
  }

  if (claim.isError) {
    return (
      <Shell>
        <ErrorState
          error={claim.error}
          onRetry={() => void claim.refetch()}
          title="Could not confirm your purchase"
          testId="license-error"
        />
      </Shell>
    );
  }

  if (!key) {
    // Still waiting for the payment, or for Polar to issue the key.
    return (
      <Shell>
        <LoadingRegion testId="license-loading">
          <Card className="border-border/50">
            <CardHeader className="text-center">
              <Logo className="mx-auto mb-2 size-10" />
              <CardTitle className="text-xl">Confirming your payment…</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-4 w-3/4" />
              {slow && (
                <p className="text-sm text-muted-foreground">
                  This is taking longer than usual. Your key is also in your receipt email; enter it
                  under Settings → Remove ads.
                </p>
              )}
            </CardContent>
          </Card>
        </LoadingRegion>
      </Shell>
    );
  }

  const copy = async () => {
    await navigator.clipboard.writeText(key);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Shell>
      <Card className="border-border/50" data-testid="license-success">
        <CardHeader className="text-center">
          <PartyPopper className="mx-auto mb-2 size-10 text-primary" />
          <CardTitle className="text-xl">
            <h1>Thank you! Ads are gone.</h1>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Your lifetime license is active in this browser.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 rounded-md bg-secondary/50 px-3 py-2">
            <code className="flex-1 truncate text-sm" data-testid="license-key">
              {key}
            </code>
            <Button variant="ghost" size="sm" onClick={() => void copy()}>
              {copied ? <Check className="text-success" /> : <Copy />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{NO_ACCOUNT_NOTE}</p>
          <Button asChild className="w-full">
            <Link to={licenseReturnPath()}>Back to planning</Link>
          </Button>
        </CardContent>
      </Card>
    </Shell>
  );
}
